import { format } from 'date-fns';
import { useFocusEffect, useLocalSearchParams, useRouter } from 'expo-router';
import * as WebBrowser from 'expo-web-browser';
import { t } from 'i18next';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Platform, Pressable, ScrollView, View } from 'react-native';
import Modal from 'react-native-modal';
import { useSharedValue } from 'react-native-reanimated';
import type { ICarouselInstance } from 'react-native-reanimated-carousel';
import Toast from 'react-native-toast-message';
import {
  createStyleSheet,
  UnistylesRuntime,
  useStyles,
} from 'react-native-unistyles';

import type { Extra } from '@/api';
import { useCustomer } from '@/api/customers/use-customer';
import { useDiscount } from '@/api/discounts/use-discount';
import type { Subscription, SubscriptionCar } from '@/api/subscriptions/types';
import { useSubscriptionPaymentCreate } from '@/api/subscriptions/use-subscription-create';
import {
  getCheapestModelKey,
  useSubscriptionOptions,
} from '@/api/subscriptions/use-subscription-options';
import { FinishProfileModal } from '@/components/checkout/finish-profile-modal';
import {
  NavigationButton,
  NavigationButtonTypes,
} from '@/components/navigation-button';
import ChooseCar from '@/components/subscription/choose-car';
import ChooseExtras from '@/components/subscription/choose-extras';
import ChooseLocationTime from '@/components/subscription/choose-location-time';
import Summary from '@/components/subscription/summary';
import {
  camelCaseToTitleCase,
  formatNumber,
  getCarContentData,
  getSubscriptionInsuranceCost,
  isDesktop,
  openAddressInMaps,
  titleize,
  useAuth,
  useCurrentOrganization,
} from '@/core';
import mixpanelService from '@/core/clients/mixpanel';
import { useTrackMetaEvent } from '@/core/hooks/use-track-meta-event';
import useSettingsStore from '@/stores/use-settings-store';
import useSubscriptionStore from '@/stores/use-subscription-store';
import { PINKLING } from '@/style/colors';
import { Button, Text } from '@/ui';
import Alert, { AlertType } from '@/ui/alert';
import { Icon } from '@/ui/icon';
import { Info } from '@/ui/icons';
import { Eternity } from '@/ui/icons/eternity';
import { QuestionMark } from '@/ui/icons/question-mark';
import { Tooltip } from '@/ui/tooltip';

import { EonOfferModal } from '../search/eon-offer-modal';

export default function Subscription() {
  const { styles, theme } = useStyles(stylesheet);
  const [showSummary, setShowSummary] = useState(false);
  const scrollRef = useRef<ScrollView>(null);
  const chooseCarRef = useRef<View>(null);
  const chooseExtrasRef = useRef<View>(null);
  const address = useSubscriptionStore((state) => state.address);
  const startDatetime = useSubscriptionStore((state) => state.startDatetime);
  const period = useSubscriptionStore((state) => state.period);
  const brandName = useSubscriptionStore((state) => state.brandName);
  const modelName = useSubscriptionStore((state) => state.modelName);
  const trimName = useSubscriptionStore((state) => state.trimName);
  const car = useSubscriptionStore((state) => state.car);
  const autopilot = useSubscriptionStore((state) => state.autopilot);
  const mileage = useSubscriptionStore((state) => state.mileage);
  const mileageCost = useSubscriptionStore((state) => state.mileageCost);
  const insurance = useSubscriptionStore((state) => state.insurance);
  const signature = useSubscriptionStore((state) => state.signature);
  const discount = useSubscriptionStore((state) => state.discount);
  const setCar = useSubscriptionStore((state) => state.setCar);
  const setDiscount = useSubscriptionStore((state) => state.setDiscount);
  const setBrandName = useSubscriptionStore((state) => state.setBrandName);
  const setModelName = useSubscriptionStore((state) => state.setModelName);
  const setTrimName = useSubscriptionStore((state) => state.setTrimName);
  const setAutopilot = useSubscriptionStore((state) => state.setAutopilot);
  const setMileage = useSubscriptionStore((state) => state.setMileage);
  const setInsurance = useSubscriptionStore((state) => state.setInsurance);
  const signingSignature = useSubscriptionStore(
    (state) => state.signingSignature
  );
  const setSigningSignature = useSubscriptionStore(
    (state) => state.setSigningSignature
  );
  const subscriptionTutorialSeen = useSettingsStore(
    (state) => state.subscriptionTutorialSeen
  );
  const reset = useSubscriptionStore((state) => state.reset);
  const [stripePaymentIntentSecret, setStripePaymentIntentSecret] =
    useState('');
  const router = useRouter();
  const navbarRef = useRef(null);
  const [navbarPosition, setNavbarPosition] = React.useState({
    top: 0,
    left: 0,
    x: 0,
    y: 0,
    width: 0,
    height: 0,
  });
  const stickyBarRef = useRef();
  const [stickyBarPosition, setStickyBarPosition] = useState({
    top: 0,
    left: 0,
    x: 0,
    y: 0,
    width: 0,
    height: 0,
  });
  const [isSticky, setIsSticky] = useState(false);
  const [contentWidth, setContentWidth] = React.useState(0);
  const carouselRef = React.useRef<ICarouselInstance>(null);
  const progress = useSharedValue<number>(0);
  const [carouselWidth, setCarouselWidth] = useState(0);
  const [showRentalBreakdownModal, setShowRentalBreakdownModal] =
    React.useState(false);
  const status = useAuth.use.status();
  const summaryRef = useRef();
  const [checkingOut, setCheckingOut] = React.useState(false);
  const [showFinishProfileModal, setShowFinishProfileModal] =
    React.useState(false);
  const resumedCheckoutRef = useRef(false);
  const [showEonOfferModal, setShowEonOfferModal] = React.useState(false);
  const local = useLocalSearchParams<{
    code?: string;
    checkout?: string;
    'skip-tutorial'?: string;
  }>();

  const skipTutorial = local['skip-tutorial'] === '1';

  // When resuming checkout after login, the subscription selections are already
  // in the store. These guards skip the mount-time reset effects below so the
  // restored selections aren't wiped on the resume remount.
  const skipResetOnResumeRef = useRef(local.checkout === 'true');
  const skipCarResetOnResumeRef = useRef(local.checkout === 'true');
  const { isEVFMC } = useCurrentOrganization();

  const { data: discountData, refetch: refetchDiscount } = useDiscount({
    enabled: false,
    variables: { code: local.code || '' },
  });

  const { data: customer } = useCustomer();

  const trackMetaEvent = useTrackMetaEvent();

  const { data: subscriptionOptions, isLoading } = useSubscriptionOptions({
    variables: {
      address: address?.description,
      startDate: startDatetime!,
    },
    enabled: !!address && !!startDatetime,
  });

  useFocusEffect(
    React.useCallback(() => {
      setSigningSignature(false);
    }, [])
  );

  useFocusEffect(
    React.useCallback(() => {
      if (status === 'signIn' && checkingOut && customer?.setupComplete) {
        setCheckingOut(false);
        setShowSummary(true);
      }
    }, [checkingOut, status, customer])
  );

  // Resume checkout after returning from /login. handleStartCheckout appends
  // ?checkout=true to the returnTo, so once the user is signed in we resume the
  // checkout intent (flip `checkingOut`) rather than jumping straight to the
  // summary — the effect above then either opens the summary (complete profile)
  // or the finish-profile gate below (incomplete). The ref keeps this to a
  // single auto-resume so re-focusing the tab doesn't reopen it. We wait for
  // `customer` to load so we don't act before we know the profile state.
  useFocusEffect(
    React.useCallback(() => {
      if (
        !resumedCheckoutRef.current &&
        local.checkout === 'true' &&
        status === 'signIn' &&
        customer
      ) {
        resumedCheckoutRef.current = true;
        setCheckingOut(true);
      }
    }, [local.checkout, status, customer])
  );

  useFocusEffect(
    React.useCallback(() => {
      if (
        status === 'signIn' &&
        checkingOut &&
        customer &&
        !customer.setupComplete
      ) {
        setShowFinishProfileModal(true);
      }
    }, [checkingOut, status, customer])
  );

  const handleStartCheckout = () => {
    if (status === 'signIn') {
      setCheckingOut(true);
    } else {
      // Carry the checkout intent through login so we can resume into the
      // summary on return (the component's local state is lost on navigation,
      // but the subscription selections live in a persistent store).
      // skip-tutorial has to ride along too: nothing persists it, so dropping it
      // here would bounce a paid-traffic signup into onboarding mid-checkout.
      const returnTo = skipTutorial
        ? '/subscriptions?checkout=true&skip-tutorial=1'
        : '/subscriptions?checkout=true';

      router.push(`/login?returnTo=${encodeURIComponent(returnTo)}`);
    }
  };

  useEffect(() => {
    if (!showSummary && !address) {
      mixpanelService.track('Page Viewed', {
        'Page Name': 'Subscription',
      });
    }
  }, [showSummary]);

  useEffect(() => {
    trackMetaEvent('ViewContent', {
      type: 'subscription',
      customData: { content_category: 'subscription' },
    });
  }, []);

  React.useEffect(() => {
    if (!subscriptionTutorialSeen && !skipTutorial) {
      setTimeout(() => {
        router.push({
          pathname: '/subscriptions/onboarding',
          ...(local.code ? { params: { code: String(local.code) } } : {}),
        });
      }, 100);
    }
  }, [subscriptionTutorialSeen, local.code, skipTutorial]);

  React.useEffect(() => {
    if (showSummary) {
      scrollRef.current?.scrollTo({ y: 0, animated: true });

      mixpanelService.track('Page Viewed', {
        'Page Name': 'Subscription Checkout',
      });

      mixpanelService.track('Subscription Customized', {
        'Subscription Period': period,
        'Subscription Model': modelName,
        'Subscription Trim': trimName,
        'Subscription Autopilot': autopilot?.title,
        'Subscription Mileage': mileage?.name,
        'Subscription Insurance': insurance?.name,
        'Subscription Discount': discount?.code || 'None',
        Price: calculatedCost,
      });
    }
  }, [showSummary]);

  React.useEffect(() => {
    if (local.code) {
      refetchDiscount();
    }
  }, [local.code]);

  React.useEffect(() => {
    if (discountData) {
      setDiscount(discountData);
    }
  }, [discountData]);

  React.useEffect(() => {
    if (discountData) {
      setShowEonOfferModal(true);
    }
  }, [discountData]);

  useEffect(() => {
    if (period && !modelName && chooseCarRef.current && scrollRef.current) {
      chooseCarRef.current.measureLayout(
        scrollRef.current.getInnerViewNode(),
        (_x, y) => {
          scrollRef.current?.scrollTo({ y, animated: true });
        }
      );
    }
  }, [period, modelName]);

  useEffect(() => {
    if (car && !autopilot && chooseExtrasRef.current && scrollRef.current) {
      chooseExtrasRef.current.measureLayout(
        scrollRef.current.getInnerViewNode(),
        (_x, y) => {
          scrollRef.current?.scrollTo({ y, animated: true });
        }
      );
    }
  }, [car, autopilot]);

  const { mutate: createSubPayment, isPending: isCreatingSubPayment } =
    useSubscriptionPaymentCreate();

  const carCost = useMemo(() => {
    if (!subscriptionOptions) return 0;

    const selectedPeriod = period ? subscriptionOptions[period] : undefined;
    if (!selectedPeriod) return 0;

    let cost = 0;

    const cheapestModelKey = getCheapestModelKey(selectedPeriod);
    const idxKey = brandName && modelName && `${brandName}${modelName}`;

    if (period && cheapestModelKey && !idxKey) {
      cost = selectedPeriod[cheapestModelKey].cheapestOption.cost;
    }

    if (period && idxKey && !trimName) {
      cost = selectedPeriod[idxKey].cheapestOption.cost;
    }

    if (period && idxKey && trimName) {
      cost = selectedPeriod[idxKey].trimChoices[trimName][0].cost;
    }

    return period === '6Months' ? cost / 6 : cost;
  }, [subscriptionOptions, period, modelName, trimName]);

  const calculatedCost = useMemo(() => {
    let cost = carCost;

    if (autopilot) {
      cost += autopilot.amount;
    }

    if (mileage) {
      cost += mileageCost;
    }

    if (insurance) {
      cost += getSubscriptionInsuranceCost(insurance, carCost, period);
    }

    if (car && car.deliveryCost > 0) {
      cost += car.deliveryCost;
    }

    return cost;
  }, [autopilot, mileage, insurance, carCost, mileageCost, car, period]);

  const deposit = useMemo(() => {
    if (!subscriptionOptions || !period) return 0;

    return subscriptionOptions[period]?.deposit ?? 0;
  }, [subscriptionOptions, period]);

  // Both paths converge on the summary — a signed-in user opens it straight
  // away, a signed-out one after logging in and resuming — so this is the first
  // point where the customer (and the PII Meta needs to match the event) is
  // guaranteed to be loaded. The ref keeps it to one fire per open.
  const initiateCheckoutFiredRef = useRef(false);

  useEffect(() => {
    if (!showSummary) {
      initiateCheckoutFiredRef.current = false;
      return;
    }
    if (!customer || initiateCheckoutFiredRef.current) return;

    initiateCheckoutFiredRef.current = true;

    const contentCar =
      brandName && modelName
        ? {
            brandName: camelCaseToTitleCase(brandName),
            modelName: camelCaseToTitleCase(modelName),
            modelType: car?.trim?.displayName,
          }
        : undefined;

    trackMetaEvent('InitiateCheckout', {
      type: 'subscription',
      customData: {
        ...(contentCar
          ? getCarContentData(contentCar, {
              category: 'subscription',
              itemPrice: calculatedCost,
            })
          : { content_category: 'subscription' }),
        currency: 'USD',
        value: calculatedCost,
      },
    });
  }, [
    showSummary,
    customer,
    trackMetaEvent,
    brandName,
    modelName,
    car,
    calculatedCost,
  ]);

  const prevPeriod = useRef(null);
  const prevModelName = useRef(modelName);
  const prevTrimName = useRef(trimName);
  const prevAutopilot = useRef(autopilot);
  const prevMileageCost = useRef(mileageCost);
  const prevInsurance = useRef(insurance);
  const prevCalculatedCost = useRef(calculatedCost);

  React.useEffect(() => {
    if (
      prevPeriod.current === null &&
      prevPeriod.current !== period &&
      period
    ) {
      mixpanelService.track('Subscription Option Selected', {
        'Option Type': 'Period',
        'Option Value': period,
        'Current Total': calculatedCost,
      });
      prevPeriod.current = period;
      prevCalculatedCost.current = calculatedCost;
    }
  }, [period, calculatedCost]);

  React.useEffect(() => {
    if (
      prevPeriod.current !== null &&
      prevPeriod.current !== period &&
      period
    ) {
      const value = subscriptionOptions[period];
      const cheapestModelKey = getCheapestModelKey(value);
      if (!value || !cheapestModelKey) {
        return;
      }

      const cheapestOption = value[cheapestModelKey]
        .cheapestOption as SubscriptionCar;

      const cheapestOptionCost =
        period === '6Months' ? cheapestOption.cost / 6 : cheapestOption.cost;

      mixpanelService.track('Subscription Option Selected', {
        'Option Type': 'Period',
        'Option Value': period,
        'Current Total': cheapestOptionCost,
      });
      prevPeriod.current = period;
      prevCalculatedCost.current = calculatedCost;
    }
  }, [period, calculatedCost]);

  React.useEffect(() => {
    if (prevModelName.current !== modelName && modelName) {
      const idxKey = brandName && modelName && `${brandName}${modelName}`;

      let cheapestOptionCost = 0;

      if (period && !idxKey) {
        cheapestOptionCost =
          subscriptionOptions[period][idxKey].cheapestOption.cost;
      }

      if (period && idxKey && !trimName) {
        cheapestOptionCost =
          subscriptionOptions[period][idxKey].cheapestOption.cost;
      }

      if (period && idxKey && trimName) {
        cheapestOptionCost =
          subscriptionOptions[period][idxKey].trimChoices[trimName][0].cost;
      }

      cheapestOptionCost =
        period === '6Months' ? cheapestOptionCost / 6 : cheapestOptionCost;

      const newTotal = cheapestOptionCost;

      mixpanelService.track('Subscription Option Selected', {
        'Option Type': 'Model Name',
        'Option Value': modelName,
        'Current Total': newTotal,
        'Price Delta': newTotal - prevCalculatedCost.current,
      });
      prevModelName.current = modelName;
      prevCalculatedCost.current = newTotal;
    }
  }, [modelName, calculatedCost]);

  React.useEffect(() => {
    if (prevModelName.current !== modelName && modelName === '') {
      prevModelName.current = modelName;
    }
  }, [modelName]);

  React.useEffect(() => {
    if (prevTrimName.current !== trimName && trimName === '') {
      prevTrimName.current = trimName;
    }
  }, [trimName]);

  React.useEffect(() => {
    if (prevTrimName.current !== trimName && trimName) {
      mixpanelService.track('Subscription Option Selected', {
        'Option Type': 'Trim Name',
        'Option Value': trimName,
        'Current Total': calculatedCost,
        'Price Delta': calculatedCost - prevCalculatedCost.current,
      });
      prevTrimName.current = trimName;
      prevCalculatedCost.current = calculatedCost;
    }
  }, [trimName, calculatedCost]);

  React.useEffect(() => {
    if (prevInsurance.current !== insurance && insurance) {
      mixpanelService.track('Subscription Option Selected', {
        'Option Type': 'Insurance',
        'Option Value': insurance.name,
        'Current Total': calculatedCost,
        'Price Delta': calculatedCost - prevCalculatedCost.current,
      });
      prevInsurance.current = insurance;
      prevCalculatedCost.current = calculatedCost;
    }
  }, [insurance, calculatedCost]);

  const showMileageInBreakdown = useMemo(() => {
    if (isEVFMC) {
      return false;
    }

    return !!mileage;
  }, [isEVFMC, mileage]);

  const onPressPagination = (index: number) => {
    carouselRef.current?.scrollTo({
      /**
       * Calculate the difference between the current index and the target index
       * to ensure that the carousel scrolls to the nearest index
       */
      count: index - progress.value,
      animated: true,
    });
  };

  useEffect(() => {
    setSigningSignature(false);
  }, [brandName, modelName, trimName, autopilot, mileage, insurance]);

  useEffect(() => {
    // When resuming checkout after login the store already holds the user's
    // selections; skip this mount-time reset so we don't wipe them. This reset
    // is only meant to clear dependent choices when the user actually changes
    // location/time/period.
    if (skipResetOnResumeRef.current) {
      skipResetOnResumeRef.current = false;
      return;
    }

    setInsurance(null);
    setMileage(null);
    setAutopilot(null);
    setCar(null);
    setModelName(null);
    setTrimName(null);
    setBrandName(null);
  }, [address, startDatetime, period]);

  useEffect(() => {
    // Same rationale as above: don't clear insurance/autopilot on the resume
    // mount, only when the car actually changes afterwards.
    if (skipCarResetOnResumeRef.current) {
      skipCarResetOnResumeRef.current = false;
      return;
    }

    setInsurance(null);
    setAutopilot(null);
  }, [car]);

  // console.log(car, autopilot, mileage, insurance);

  const renderOverview = () => {
    return (
      <View style={{ gap: 16 }}>
        <View
          style={{
            backgroundColor: theme.colors.background_2,
            gap: 16,
            padding: 16,
            borderRadius: 16,
            borderColor: theme.colors.card.default.borderColor,
            borderWidth: 1,
          }}
        >
          <Text type="title2">
            {period && period !== 'weekly'
              ? 'Delivery Details'
              : 'Pickup Location'}
          </Text>

          <View
            style={{
              borderTopColor: theme.colors.card.default.borderColor,
              borderTopWidth: 1,
              paddingTop: 16,
            }}
          >
            {!address || !startDatetime ? (
              <View>
                <Text
                  type="bodyRegular"
                  style={{ color: theme.colors.text.secondary }}
                >
                  Select your pickup location and start date to see available
                  cars and pricing.
                </Text>
              </View>
            ) : (
              <View
                style={{
                  flex: 1,
                  gap: 16,
                }}
              >
                <View>
                  <Text
                    tx={
                      period !== 'weekly'
                        ? 'carId.deliveryLocation'
                        : 'carId.pickupLocation'
                    }
                    type="bodyRegular"
                  />

                  <Text
                    type="bodyRegular"
                    style={{ color: theme.colors.text.secondary }}
                  >
                    {format(startDatetime, 'MMM dd yyyy')}
                  </Text>

                  {period === 'weekly' && !car ? (
                    <Text
                      type="bodyRegular"
                      style={{
                        color: theme.colors.text.secondary,
                      }}
                    >
                      Select a model first
                    </Text>
                  ) : (
                    <Pressable
                      onPress={() => {
                        if (period === 'weekly' && car) {
                          openAddressInMaps(car.defaultLocation.address);
                        } else {
                          openAddressInMaps(address.description);
                        }
                      }}
                    >
                      <Text
                        type="bodyRegular"
                        style={{
                          color: theme.colors.text.secondary,
                          textDecorationLine: 'underline',
                        }}
                      >
                        {period === 'weekly' && car
                          ? car.defaultLocation.address
                          : address.description}
                      </Text>
                    </Pressable>
                  )}
                </View>
              </View>
            )}
          </View>
        </View>

        <View
          style={{
            backgroundColor: theme.colors.background_2,
            gap: 16,
            padding: 16,
            borderRadius: 16,
            borderColor: theme.colors.card.default.borderColor,
            borderWidth: 1,
          }}
        >
          {period ? (
            <View style={{ gap: 16 }}>
              <Text tx="subscriptions.overviewTitle" type="title2" />

              <View
                style={{
                  borderTopColor: theme.colors.card.default.borderColor,
                  borderTopWidth: 1,
                  paddingTop: 16,
                  gap: 16,
                }}
              >
                {isEVFMC ? (
                  <Alert
                    type={AlertType.BRAND}
                    title="Unlimited mileage, always included."
                    body="Every subscription comes with unlimited miles. No caps, no overage fees, ever."
                    iconWidth={32}
                    iconHeight={32}
                    icon={<Eternity />}
                    iconContainerStyle={{ alignSelf: 'center' }}
                  />
                ) : null}

                <View style={{ gap: 4 }}>
                  {carCost !== 0 && (
                    <View
                      style={{
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Text
                        tx="subscriptions.overviewCommitment"
                        values={{
                          period:
                            period === '6Months' ? 'Monthly' : titleize(period),
                        }}
                        type="bodyRegular"
                      />

                      <Text
                        type="bodyRegular"
                        style={{ color: theme.colors.text.secondary }}
                      >
                        {`$${formatNumber(carCost)}`}
                      </Text>
                    </View>
                  )}

                  {autopilot && (
                    <View
                      style={{
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Text type="bodyRegular">{autopilot?.title}</Text>

                      <Text
                        type="bodyRegular"
                        style={{ color: theme.colors.text.secondary }}
                      >
                        {autopilot?.amount !== 0
                          ? `$${formatNumber(autopilot?.amount)}`
                          : 'Included'}
                      </Text>
                    </View>
                  )}

                  {showMileageInBreakdown && (
                    <View
                      style={{
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Text
                        tx="subscriptions.overviewMileage"
                        values={{
                          miles: mileage.name,
                          period: subscriptionOptions?.[period].unit,
                        }}
                        type="bodyRegular"
                      />

                      <Text
                        type="bodyRegular"
                        style={{ color: theme.colors.text.secondary }}
                      >
                        {`$${formatNumber(mileageCost)}`}
                      </Text>
                    </View>
                  )}

                  {insurance && (
                    <View
                      style={{
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Text type="bodyRegular">{insurance?.name}</Text>

                      <Text
                        type="bodyRegular"
                        style={{ color: theme.colors.text.secondary }}
                      >
                        {`$${
                          insurance
                            ? `${formatNumber(
                                getSubscriptionInsuranceCost(
                                  insurance,
                                  carCost,
                                  period
                                )
                              )}`
                            : 'Free'
                        }`}
                      </Text>
                    </View>
                  )}

                  {discount && (
                    <View
                      style={{
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Text type="bodyRegular" style={{ color: PINKLING }}>
                        {`Discount: ${discount.title}`}
                      </Text>

                      <Text type="bodyRegular" style={{ color: PINKLING }}>
                        {`-$${formatNumber(discount.amount)}`}
                      </Text>
                    </View>
                  )}
                </View>

                {calculatedCost > 0 ? (
                  <View
                    style={{
                      borderTopColor: theme.colors.card.default.borderColor,
                      borderTopWidth: 1,
                      gap: 16,
                    }}
                  >
                    <View style={{ paddingTop: 16 }}>
                      <View
                        style={{
                          flexDirection: 'row',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Text type="bodyRegular">Total price</Text>

                        <View
                          style={{
                            flexDirection: 'row',
                            alignItems: 'center',
                          }}
                        >
                          <Text type="bodyRegular">
                            $
                            {formatNumber(
                              calculatedCost - (car?.deliveryCost ?? 0)
                            ) || '-'}
                          </Text>

                          <Text type="bodyRegular">
                            {`/${
                              (period && subscriptionOptions?.[period].unit) ||
                              'month'
                            }`}
                          </Text>
                        </View>
                      </View>

                      {car && car?.deliveryCost > 0 && (
                        <View
                          style={{
                            flexDirection: 'row',
                            justifyContent: 'space-between',
                          }}
                        >
                          <View
                            style={{
                              flexDirection: 'row',
                              gap: 8,
                              alignItems: 'center',
                            }}
                          >
                            <Text
                              type="bodyRegular"
                              tx="subscriptions.overviewDeliveryFee"
                            />

                            <Tooltip
                              text={t(
                                'subscriptions.overviewDeliveryFeeTooltip'
                              )}
                            />
                          </View>

                          <Text type="bodyRegular">
                            +${formatNumber(car.deliveryCost)}
                          </Text>
                        </View>
                      )}

                      {!isEVFMC ? (
                        <View
                          style={{
                            flexDirection: 'row',
                            justifyContent: 'space-between',
                          }}
                        >
                          <Text type="bodyRegular">
                            One-Time Initiation Fee
                          </Text>

                          <Text type="bodyRegular">+$99</Text>
                        </View>
                      ) : null}

                      {!isEVFMC ? (
                        <View
                          style={{
                            flexDirection: 'row',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          }}
                        >
                          <Text type="bodyRegular" style={{ flex: 1 }}>
                            One-Time Refundable Deposit
                          </Text>

                          <Text type="bodyRegular">
                            ${formatNumber(deposit)}
                          </Text>
                        </View>
                      ) : null}
                    </View>

                    <View>
                      <Button
                        text="Checkout"
                        type="primary"
                        onPress={handleStartCheckout}
                        style={{ width: '100%' }}
                        disabled={!car || !autopilot || !mileage || !insurance}
                      />
                    </View>
                  </View>
                ) : null}
              </View>
            </View>
          ) : (
            <View style={{ gap: 16 }}>
              <Text type="title2">Subscription Overview</Text>

              {address && startDatetime && !period ? (
                <Text
                  type="bodyRegular"
                  style={{ color: theme.colors.text.secondary }}
                >
                  Select a subscription period to see available cars and
                  pricing.
                </Text>
              ) : (
                <Text
                  type="bodyRegular"
                  style={{ color: theme.colors.text.secondary }}
                >
                  Select your pickup location and start date to see available
                  cars and pricing.
                </Text>
              )}

              <Button
                text="Checkout"
                type="primary"
                onPress={() => {}}
                style={{ width: '100%' }}
                disabled
              />
            </View>
          )}
        </View>
      </View>
    );
  };

  if (isDesktop()) {
    return (
      <View style={styles.container}>
        <View
          ref={navbarRef}
          style={{
            height: 20,
          }}
          onLayout={(event) => {
            if (navbarRef.current) {
              navbarRef.current.measure((x, y, width, height, pageX, pageY) => {
                setNavbarPosition({
                  top: pageY,
                  left: pageX,
                  x,
                  y,
                  width,
                  height,
                });
              });
            }
          }}
        />

        <View style={styles.scrollViewContainer}>
          <ScrollView
            ref={scrollRef}
            showsVerticalScrollIndicator={false}
            style={{
              width: '100%',
            }}
            contentContainerStyle={{
              alignItems: 'center',
            }}
            scrollEventThrottle={4}
            onScroll={(event) => {
              const scrollY = event.nativeEvent.contentOffset.y;

              if (
                scrollY >= stickyBarPosition.top - navbarPosition.height &&
                !isSticky
              ) {
                setIsSticky(true);
              } else if (
                scrollY < stickyBarPosition.top - navbarPosition.height &&
                isSticky
              ) {
                setIsSticky(false);
              }
            }}
          >
            <View
              style={styles.scrollViewChild}
              onLayout={(event) => {
                const { width } = event.nativeEvent.layout;
                setContentWidth(width);
              }}
            >
              <View style={{ flexDirection: 'row', gap: 32 }}>
                <View
                  style={{
                    gap: 32,
                    paddingTop: 16,
                    width: showSummary ? '100%' : '80%',
                  }}
                >
                  {showSummary && (
                    <View>
                      <NavigationButton
                        type={NavigationButtonTypes.back_with_text}
                        onPress={() => {
                          if (showSummary) {
                            setStripePaymentIntentSecret('');
                          }

                          setShowSummary(false);
                        }}
                      />
                    </View>
                  )}

                  {!showSummary && (
                    <View style={{ gap: 32, maxWidth: 600 }}>
                      <View style={{ gap: 16 }}>
                        <View style={{ gap: 8 }}>
                          <Text
                            tx="subscriptions.customizeSubscription"
                            type="title1"
                          />

                          <Text
                            tx="subscriptions.customSubscriptionSubtitle"
                            type="bodyRegular"
                            style={{
                              color: theme.colors.text.secondary,
                            }}
                          />
                        </View>
                      </View>
                    </View>
                  )}

                  <View
                    style={[
                      styles.stepContainer,
                      showSummary && {
                        minWidth: '100%',
                        alignItems: 'center',
                        maxWidth: 800,
                      },
                    ]}
                  >
                    {!showSummary ? <ChooseLocationTime /> : null}

                    {period && !showSummary ? (
                      <View ref={chooseCarRef}>
                        <ChooseCar />
                      </View>
                    ) : null}

                    {car && !showSummary ? (
                      <View ref={chooseExtrasRef}>
                        <ChooseExtras
                          onAutopilotChange={(selectedAutopilot: Extra) => {
                            const newTotal =
                              (prevCalculatedCost.current - autopilot?.amount ||
                                0) + selectedAutopilot.amount;

                            mixpanelService.track(
                              'Subscription Option Selected',
                              {
                                'Option Type': 'Autopilot',
                                'Option Value': selectedAutopilot.title,
                                'Current Total': newTotal,
                                'Price Delta':
                                  newTotal - prevCalculatedCost.current,
                              }
                            );
                            prevAutopilot.current = selectedAutopilot;
                            prevCalculatedCost.current = newTotal;
                          }}
                          onMileageChange={(selectedMileage: {
                            name: string;
                            amount: number;
                          }) => {
                            const newTotal =
                              (prevCalculatedCost.current - mileageCost || 0) +
                              selectedMileage.amount;

                            mixpanelService.track(
                              'Subscription Option Selected',
                              {
                                'Option Type': 'Mileage',
                                'Option Value': selectedMileage.name,
                                'Current Total': newTotal,
                                'Price Delta':
                                  newTotal - prevCalculatedCost.current,
                              }
                            );
                            prevMileageCost.current = mileageCost;
                            prevCalculatedCost.current = newTotal;
                          }}
                        />
                      </View>
                    ) : null}

                    {showSummary ? (
                      <Summary
                        ref={summaryRef}
                        onPaySuccess={() => {
                          Toast.show({
                            text1: 'Payment successful!',
                            type: 'success',
                          });

                          router.push('/subscriptions/thank-you');

                          setShowSummary(false);
                        }}
                      />
                    ) : null}
                  </View>

                  {address && startDatetime && !showSummary ? (
                    <View
                      style={{
                        flexDirection: 'row',
                        gap: 4,
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      <Icon
                        type="naked"
                        icon={<QuestionMark />}
                        fillColor={theme.colors.icon.color}
                        width={12}
                        height={12}
                      />

                      <Text
                        type="caption1"
                        style={{ color: theme.colors.text.secondary }}
                      >
                        Questions about subscriptions?
                      </Text>

                      <Pressable
                        onPress={async () => {
                          const baseUrl =
                            'https://www.eonrides.com/legal/subscription-faq';

                          if (Platform.OS === 'web') {
                            window.open(baseUrl, '_blank');
                          } else {
                            await WebBrowser.openBrowserAsync(baseUrl);
                          }
                        }}
                      >
                        <Text
                          type="caption1"
                          style={{
                            color: theme.colors.text.secondary,
                            textDecorationLine: 'underline',
                          }}
                        >
                          View FAQ
                        </Text>
                      </Pressable>
                    </View>
                  ) : null}
                </View>

                {!showSummary && !isSticky && (
                  <View
                    style={{
                      gap: 16,
                      width: contentWidth * 0.4,
                      paddingTop: 16,
                    }}
                    ref={stickyBarRef}
                    onLayout={(e) => {
                      if (stickyBarRef.current && !isSticky) {
                        stickyBarRef.current.measure(
                          (x, y, width, height, pageX, pageY) => {
                            setStickyBarPosition({
                              top: pageY,
                              left: pageX,
                              x,
                              y,
                            });
                          }
                        );
                      }
                    }}
                  >
                    <View>{renderOverview()}</View>
                  </View>
                )}
              </View>
            </View>
          </ScrollView>

          {!showSummary && isSticky ? (
            <View
              style={{
                width: contentWidth * 0.4,
                height: '100%',
                position: 'absolute',
                top: navbarPosition.top + navbarPosition.height,
                left: stickyBarPosition.left,
                zIndex: 1000,
                gap: 24,
              }}
              // pointerEvents="none"
            >
              <View>{renderOverview()}</View>
            </View>
          ) : null}
        </View>

        <EonOfferModal
          visible={showEonOfferModal && subscriptionTutorialSeen}
          onClose={() => {
            setShowEonOfferModal(false);
          }}
          onClick={async () => {
            setShowEonOfferModal(false);
          }}
          title={discount?.meta?.title}
          body={discount?.meta?.body}
          code={discount?.code}
          expiresAt={discount?.endDate}
        />

        <FinishProfileModal
          visible={showFinishProfileModal}
          onClose={() => {
            // Cancelling abandons the checkout intent so re-focusing the tab
            // doesn't immediately reopen the gate.
            setShowFinishProfileModal(false);
            setCheckingOut(false);
          }}
          onDone={() => {
            // Profile is now complete; the checkingOut effect advances to the
            // summary once the refetched customer reflects setupComplete.
            setShowFinishProfileModal(false);
          }}
        />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.scrollViewContainer}>
        <ScrollView
          ref={scrollRef}
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={false}
          scrollEnabled={!signingSignature}
          scrollEventThrottle={4}
        >
          <View style={{ flexGrow: 1, gap: 32 }}>
            <View
              style={{
                gap: 24,
                paddingTop: 16,
                width: '100%',
                paddingBottom: !showSummary ? 100 : 0,
              }}
            >
              {showSummary && (
                <View>
                  <NavigationButton
                    type={NavigationButtonTypes.back_with_text}
                    onPress={() => {
                      if (showSummary) {
                        setStripePaymentIntentSecret('');
                      }

                      setShowSummary(false);
                    }}
                  />
                </View>
              )}

              {!showSummary && (
                <View style={{ gap: 48, maxWidth: 600 }}>
                  <View style={{ gap: 16 }}>
                    <View style={{ gap: 8 }}>
                      <Text
                        tx="subscriptions.customizeSubscription"
                        type="title1"
                      />

                      <Text
                        tx="subscriptions.customSubscriptionSubtitle"
                        type="bodyRegular"
                        style={{
                          color: theme.colors.text.secondary,
                        }}
                      />
                    </View>
                  </View>
                </View>
              )}

              <View style={styles.stepContainer}>
                {!showSummary ? <ChooseLocationTime /> : null}

                {period && !showSummary ? (
                  <View ref={chooseCarRef}>
                    <ChooseCar />
                  </View>
                ) : null}

                {car && !showSummary ? (
                  <View ref={chooseExtrasRef}>
                    <ChooseExtras
                      onAutopilotChange={(selectedAutopilot: Extra) => {
                        const newTotal =
                          (prevCalculatedCost.current - autopilot?.amount ||
                            0) + selectedAutopilot.amount;

                        mixpanelService.track('Subscription Option Selected', {
                          'Option Type': 'Autopilot',
                          'Option Value': selectedAutopilot.title,
                          'Current Total': newTotal,
                          'Price Delta': newTotal - prevCalculatedCost.current,
                        });
                        prevAutopilot.current = selectedAutopilot;
                        prevCalculatedCost.current = newTotal;
                      }}
                      onMileageChange={(selectedMileage: {
                        name: string;
                        amount: number;
                      }) => {
                        if (!showSummary) {
                          const newTotal =
                            (prevCalculatedCost.current - mileageCost || 0) +
                            selectedMileage.amount;

                          mixpanelService.track(
                            'Subscription Option Selected',
                            {
                              'Option Type': 'Mileage',
                              'Option Value': selectedMileage.name,
                              'Current Total': newTotal,
                              'Price Delta':
                                newTotal - prevCalculatedCost.current,
                            }
                          );
                          prevMileageCost.current = mileageCost;
                          prevCalculatedCost.current = newTotal;
                        }
                      }}
                    />
                  </View>
                ) : null}

                {showSummary ? (
                  <Summary
                    ref={summaryRef}
                    onPaySuccess={() => {
                      Toast.show({
                        text1: 'Payment successful!',
                        type: 'success',
                      });

                      router.push('/subscriptions/thank-you');

                      setShowSummary(false);
                    }}
                  />
                ) : null}
              </View>

              {address && startDatetime && !showSummary ? (
                <View
                  style={{
                    flexDirection: 'row',
                    gap: 4,
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <Icon
                    type="naked"
                    icon={<QuestionMark />}
                    fillColor={theme.colors.icon.color}
                    width={12}
                    height={12}
                  />

                  <Text
                    type="caption1"
                    style={{ color: theme.colors.text.secondary }}
                  >
                    Questions about subscriptions?
                  </Text>

                  <Pressable
                    onPress={async () => {
                      const baseUrl =
                        'https://www.eonrides.com/legal/subscription-faq';

                      if (Platform.OS === 'web') {
                        window.open(baseUrl, '_blank');
                      } else {
                        await WebBrowser.openBrowserAsync(baseUrl);
                      }
                    }}
                  >
                    <Text
                      type="caption1"
                      style={{
                        color: theme.colors.text.secondary,
                        textDecorationLine: 'underline',
                      }}
                    >
                      View FAQ
                    </Text>
                  </Pressable>
                </View>
              ) : null}
            </View>
          </View>
        </ScrollView>
      </View>

      {startDatetime && address && (
        <View style={styles.overviewContainer}>
          <View
            style={{
              width: '100%',
              flexGrow: 1,
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              backgroundColor: theme.colors.background_2,
              gap: 16,
              padding: 16,
            }}
          >
            <View style={{ justifyContent: 'center' }}>
              {!period && startDatetime && address ? (
                <View style={{ width: 175 }}>
                  <Text
                    type="caption1"
                    style={{ color: theme.colors.text.secondary }}
                  >
                    Select a subscription period to see available cars and
                    pricing
                  </Text>
                </View>
              ) : (
                <>
                  <View
                    style={{
                      flexDirection: 'row',
                      gap: 8,
                      alignItems: 'center',
                    }}
                  >
                    <View
                      style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                      }}
                    >
                      <Text type="bodyRegular">{`$${
                        formatNumber(
                          calculatedCost -
                            (car?.deliveryCost ?? 0) -
                            (discount?.amount || 0)
                        ) || '-'
                      } `}</Text>

                      <Text>
                        {`/ ${
                          (period && subscriptionOptions?.[period].unit) ||
                          'month'
                        }`}
                      </Text>
                    </View>

                    <Pressable
                      onPress={() => {
                        setShowRentalBreakdownModal(true);
                      }}
                      hitSlop={8}
                    >
                      <Icon
                        type="naked"
                        icon={<Info />}
                        fillColor={theme.colors.icon.color}
                        width={15}
                        height={15}
                      />
                    </Pressable>
                  </View>

                  <View>
                    <Text
                      type="caption1"
                      style={{
                        color: theme.colors.text.secondary,
                      }}
                    >
                      {`Total $${formatNumber(
                        calculatedCost +
                          (isEVFMC ? 0 : 99) -
                          (discount?.amount || 0)
                      )}`}
                    </Text>
                  </View>
                </>
              )}
            </View>

            <Button
              style={{ width: 160 }}
              tx={'Next'}
              type="primary"
              onPress={() => {
                if (showSummary) {
                  summaryRef.current?.pay();

                  return;
                }

                if (!showSummary) {
                  handleStartCheckout();

                  return;
                }
              }}
              disabled={!car || !autopilot || !mileage || !insurance}
            />
          </View>
        </View>
      )}

      <Modal
        animationIn="slideInUp"
        animationInTiming={300}
        animationOut="slideOutDown"
        animationOutTiming={300}
        isVisible={showRentalBreakdownModal}
        hasBackdrop
        style={{
          justifyContent: 'flex-end',
          margin: 0,
        }}
        onBackdropPress={() => {
          setShowRentalBreakdownModal(false);
        }}
      >
        <View
          style={{
            backgroundColor: theme.colors.background_2,
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            paddingBottom: 24,
          }}
        >
          <View
            style={{
              width: '100%',
              alignSelf: 'center',
              marginTop: 16,
              flexDirection: 'row',
              justifyContent: 'center',
              paddingHorizontal: 16,
              paddingBottom: 16,
              borderBottomColor: theme.colors.card.default.borderColor,
              borderBottomWidth: 1,
            }}
          >
            <Text type="title2">Subscription Overview</Text>

            <NavigationButton
              type={NavigationButtonTypes.cancel}
              onPress={() => {
                setShowRentalBreakdownModal(false);
              }}
              style={{
                position: 'absolute',
                right: 16,
              }}
            />
          </View>

          <View
            style={{
              padding: 16,
              gap: 8,
            }}
          >
            <View style={{ gap: 4 }}>
              {carCost !== 0 && (
                <View
                  style={{
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                  }}
                >
                  <Text
                    tx="subscriptions.overviewCommitment"
                    values={{
                      period:
                        period === '6Months' ? 'Monthly' : titleize(period),
                    }}
                    type="bodyRegular"
                    style={{ flex: 1 }}
                  />

                  <Text
                    type="bodyRegular"
                    style={{ color: theme.colors.text.secondary }}
                  >
                    {`$${formatNumber(carCost)}`}
                  </Text>
                </View>
              )}

              {autopilot && (
                <View
                  style={{
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                  }}
                >
                  <Text type="bodyRegular">{autopilot?.title}</Text>

                  <Text
                    type="bodyRegular"
                    style={{ color: theme.colors.text.secondary }}
                  >
                    {autopilot?.amount !== 0
                      ? `$${formatNumber(autopilot?.amount)}`
                      : 'Included'}
                  </Text>
                </View>
              )}

              {showMileageInBreakdown && (
                <View
                  style={{
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                  }}
                >
                  <Text
                    tx="subscriptions.overviewMileage"
                    values={{
                      miles: mileage.name,
                      period: subscriptionOptions?.[period].unit,
                    }}
                    type="bodyRegular"
                  />

                  <Text
                    type="bodyRegular"
                    style={{ color: theme.colors.text.secondary }}
                  >
                    {`$${formatNumber(mileageCost)}`}
                  </Text>
                </View>
              )}

              {insurance && (
                <View
                  style={{
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                  }}
                >
                  <Text type="bodyRegular">{insurance?.name}</Text>

                  <Text
                    type="bodyRegular"
                    style={{ color: theme.colors.text.secondary }}
                  >
                    {`$${
                      insurance
                        ? `${formatNumber(
                            getSubscriptionInsuranceCost(
                              insurance,
                              carCost,
                              period
                            )
                          )}`
                        : 'Free'
                    }`}
                  </Text>
                </View>
              )}

              {discount && (
                <View
                  style={{
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                  }}
                >
                  <Text type="bodyRegular" style={{ color: PINKLING }}>
                    {`Discount: ${discount.title}`}
                  </Text>

                  <Text type="bodyRegular" style={{ color: PINKLING }}>
                    {`-$${formatNumber(discount.amount)}`}
                  </Text>
                </View>
              )}
            </View>

            {!isEVFMC || (car && car.deliveryCost > 0) ? (
              <View
                style={{
                  borderTopColor: theme.colors.card.default.borderColor,
                  borderTopWidth: 1,
                  paddingTop: 16,
                  gap: 4,
                }}
              >
                {!isEVFMC ? (
                  <View
                    style={{
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Text type="bodyRegular">One-Time Initiation Fee</Text>

                    <Text
                      type="bodyRegular"
                      style={{ color: theme.colors.text.secondary }}
                    >
                      +$99
                    </Text>
                  </View>
                ) : null}

                {car && car?.deliveryCost > 0 && (
                  <View
                    style={{
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                    }}
                  >
                    <View
                      style={{
                        flexDirection: 'row',
                        gap: 8,
                        alignItems: 'center',
                      }}
                    >
                      <Text
                        type="bodyRegular"
                        tx="subscriptions.overviewDeliveryFee"
                      />

                      <Tooltip
                        text={t('subscriptions.overviewDeliveryFeeTooltip')}
                      />
                    </View>

                    <Text
                      type="bodyRegular"
                      style={{ color: theme.colors.text.secondary }}
                    >
                      {`+$${formatNumber(car.deliveryCost)}`}
                    </Text>
                  </View>
                )}

                {!isEVFMC ? (
                  <View
                    style={{
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Text type="bodyRegular">One-Time Security Deposit</Text>

                    <Text
                      type="bodyRegular"
                      style={{ color: theme.colors.text.secondary }}
                    >
                      {`$${formatNumber(deposit)}`}
                    </Text>
                  </View>
                ) : null}
              </View>
            ) : null}

            <View
              style={{
                borderTopColor: theme.colors.card.default.borderColor,
                borderTopWidth: 1,
                paddingTop: 16,
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <View>
                <Text type="title2">{`Total $${formatNumber(
                  calculatedCost + (isEVFMC ? 0 : 99) - (discount?.amount || 0)
                )}`}</Text>

                <Text
                  type="caption1"
                  style={{ color: theme.colors.text.secondary }}
                >
                  {`$${formatNumber(
                    calculatedCost -
                      (car?.deliveryCost ?? 0) -
                      (discount?.amount || 0)
                  )} / ${period === 'weekly' ? 'week' : 'month'}`}
                </Text>
              </View>

              <Button
                style={{ width: 160 }}
                text="Next"
                type="primary"
                onPress={() => {
                  setShowRentalBreakdownModal(false);

                  if (showSummary) {
                    summaryRef.current?.pay();

                    return;
                  }

                  handleStartCheckout();
                }}
                disabled={!car || !autopilot || !mileage || !insurance}
              />
            </View>
          </View>
        </View>
      </Modal>

      <EonOfferModal
        visible={showEonOfferModal && subscriptionTutorialSeen}
        onClose={() => {
          setShowEonOfferModal(false);
        }}
        onClick={async () => {
          setShowEonOfferModal(false);
        }}
        title={discount?.meta?.title}
        body={discount?.meta?.body}
        code={discount?.code}
        expiresAt={discount?.endDate}
      />

      <FinishProfileModal
        visible={showFinishProfileModal}
        onClose={() => {
          // Cancelling abandons the checkout intent so re-focusing the tab
          // doesn't immediately reopen the gate.
          setShowFinishProfileModal(false);
          setCheckingOut(false);
        }}
        onDone={() => {
          // Profile is now complete; the checkingOut effect advances to the
          // summary once the refetched customer reflects setupComplete.
          setShowFinishProfileModal(false);
        }}
      />
    </View>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background_1,
    paddingTop: UnistylesRuntime.insets.top,
  },
  scrollViewContainer: {
    paddingLeft: 16,
    paddingRight: 16,
    flexGrow: 1,
    height: {
      xs: '85%',
      md: '100%',
    },
    width: '100%',
  },
  scrollViewChild: {
    gap: 32,
    width: {
      xs: '100%',
      md: 1000,
    },
    flexGrow: 1,
  },
  stepContainer: {
    // minWidth: {
    //   md: 600,
    // },
    // maxWidth: {
    //   md: 600,
    // },
    flexGrow: 1,
    gap: 24,
  },
  overviewContainer: {
    flexGrow: 1,
    backgroundColor: theme.colors.background_2,
    borderTopWidth: 1,
    borderTopColor: theme.colors.card.default.borderColor,
  },
}));
